export interface DiscountablePrice {
  price: number;
  originalPrice?: number;
  discount?: number;
}

export interface ResolvedDiscountPricing {
  price: number;
  originalPrice: number | null;
  discount: number;
  hasDiscount: boolean;
}

function roundMoney(value: number) {
  return Number(value.toFixed(2));
}

export function normalizeDiscountPercentage(value: unknown) {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed)) {
    return 0;
  }

  const normalized = Math.trunc(parsed);
  if (normalized < 0) {
    return 0;
  }

  if (normalized > 99) {
    return 99;
  }

  return normalized;
}

export function applyDiscountPercentage(price: number, discount: number) {
  if (!Number.isFinite(price) || price <= 0) {
    return 0;
  }

  const normalizedDiscount = normalizeDiscountPercentage(discount);
  if (normalizedDiscount <= 0) {
    return roundMoney(price);
  }

  return roundMoney(price * (1 - normalizedDiscount / 100));
}

export function resolveDiscountPricing({
  price,
  originalPrice,
  discount,
}: DiscountablePrice): ResolvedDiscountPricing {
  const normalizedPrice = Number.isFinite(price) && price > 0 ? roundMoney(price) : 0;
  const normalizedOriginalPrice =
    Number.isFinite(originalPrice) && Number(originalPrice) > normalizedPrice
      ? roundMoney(Number(originalPrice))
      : null;
  const explicitDiscount = normalizeDiscountPercentage(discount);

  if (explicitDiscount > 0) {
    const resolvedOriginalPrice =
      normalizedOriginalPrice
      ?? (explicitDiscount < 100
        ? roundMoney(normalizedPrice / (1 - explicitDiscount / 100))
        : null);

    if (resolvedOriginalPrice && resolvedOriginalPrice > normalizedPrice) {
      return {
        price: normalizedPrice,
        originalPrice: resolvedOriginalPrice,
        discount: explicitDiscount,
        hasDiscount: true,
      };
    }
  }

  if (normalizedOriginalPrice) {
    const inferredDiscount = normalizeDiscountPercentage(
      Math.round(((normalizedOriginalPrice - normalizedPrice) / normalizedOriginalPrice) * 100)
    );

    if (inferredDiscount > 0) {
      return {
        price: normalizedPrice,
        originalPrice: normalizedOriginalPrice,
        discount: inferredDiscount,
        hasDiscount: true,
      };
    }
  }

  return {
    price: normalizedPrice,
    originalPrice: null,
    discount: 0,
    hasDiscount: false,
  };
}
